import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { LifeBuoy, Loader2, RefreshCw, Send, Undo2, User, Bot, History } from 'lucide-react';
import { API_BASE_URL } from '../../services/apiClient';

const formatRelative = (iso) => {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'justo ahora';
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours} h`;
  return `hace ${Math.floor(hours / 24)} d`;
};

const lastUserMessage = (recentMessages) => {
  const last = [...(recentMessages || [])].reverse().find((m) => m.role === 'user');
  return last?.content || '(sin mensajes)';
};

export default function SupportCasesPanel({ authToken }) {
  const [queue, setQueue] = useState([]);
  const [myCase, setMyCase] = useState(null);
  const [answered, setAnswered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState(null);
  const [releasing, setReleasing] = useState(false);
  const [answerText, setAnswerText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const headers = { Authorization: `Bearer ${authToken}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [queueRes, mineRes, answeredRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/support/cases`, { headers, params: { status: 'open' } }),
        axios.get(`${API_BASE_URL}/api/admin/support/cases/mine`, { headers }),
        axios.get(`${API_BASE_URL}/api/admin/support/cases`, { headers, params: { status: 'answered', mine: true } }),
      ]);
      setQueue(queueRes.data.cases || []);
      setAnswered((answeredRes.data.cases || []).slice(0, 5));

      if (mineRes.data.case) {
        const detail = await axios.get(`${API_BASE_URL}/api/admin/support/cases/${mineRes.data.case.id}`, { headers });
        setMyCase(detail.data);
      } else {
        setMyCase(null);
      }
    } catch (err) {
      console.error('[SupportCasesPanel] Error fetching cases:', err);
      toast.error('No se pudieron cargar los casos de soporte');
    } finally {
      setLoading(false);
    }
  }, [authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleClaim = async (caseId) => {
    setClaimingId(caseId);
    try {
      await axios.post(`${API_BASE_URL}/api/admin/support/cases/${caseId}/claim`, {}, { headers });
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo tomar el caso');
      await fetchAll();
    } finally {
      setClaimingId(null);
    }
  };

  const handleRelease = async () => {
    if (!myCase) return;
    setReleasing(true);
    try {
      await axios.post(`${API_BASE_URL}/api/admin/support/cases/${myCase.id}/release`, {}, { headers });
      setAnswerText('');
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo liberar el caso');
    } finally {
      setReleasing(false);
    }
  };

  const handleAnswer = async () => {
    if (!myCase || !answerText.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/support/cases/${myCase.id}/answer`,
        { answer: answerText.trim() },
        { headers }
      );
      toast.success('Respuesta enviada. El usuario la verá en su próximo chequeo del chat.');
      setAnswerText('');
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar la respuesta');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-slate-400 text-sm">
          Casos escalados desde el chatbot. Solo puedes tener un caso en análisis a la vez.
        </p>
        <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} className="border-slate-700 text-slate-300">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Actualizar
        </Button>
      </div>

      {/* My active case */}
      {myCase && (
        <div className="bg-slate-900/50 border border-blue-500/30 rounded-xl p-6" data-testid="support-my-case">
          <h2 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
            <LifeBuoy className="w-5 h-5 text-blue-400" /> Mi caso en análisis
          </h2>
          <p className="text-slate-500 text-xs mb-4">{myCase.user_email} · tomado {formatRelative(myCase.claimed_at)}</p>

          <div className="space-y-2 max-h-[40vh] overflow-y-auto mb-4 bg-slate-950/40 rounded-lg p-3">
            {(myCase.messages || []).map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-start' : 'justify-end'}`}>
                <div
                  className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words flex items-start gap-1.5 ${
                    m.role === 'user' ? 'bg-slate-800 text-slate-200' : 'bg-blue-600/80 text-white'
                  }`}
                >
                  {m.role === 'user' ? <User className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : <Bot className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
                  {m.content}
                </div>
              </div>
            ))}
          </div>

          <textarea
            value={answerText}
            onChange={(e) => setAnswerText(e.target.value)}
            placeholder="Escribe la respuesta para el usuario…"
            rows={3}
            data-testid="support-answer-textarea"
            className="w-full resize-none px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
          />
          <div className="flex gap-2">
            <Button
              onClick={handleAnswer}
              disabled={submitting || !answerText.trim()}
              data-testid="support-answer-submit"
              className="bg-blue-600 hover:bg-blue-500 text-white text-sm"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Enviar respuesta
            </Button>
            <Button
              variant="outline"
              onClick={handleRelease}
              disabled={releasing}
              className="border-slate-700 text-slate-300 text-sm"
            >
              {releasing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Undo2 className="w-4 h-4 mr-1" />}
              Liberar caso
            </Button>
          </div>
        </div>
      )}

      {/* Open queue */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <LifeBuoy className="w-5 h-5 text-amber-400" /> Casos abiertos ({queue.length})
        </h2>
        {queue.length === 0 ? (
          <p className="text-slate-500 text-sm py-4 text-center">No hay casos pendientes</p>
        ) : (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {queue.map((c) => (
              <div key={c.id} className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg gap-3" data-testid="support-queue-item">
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">{c.user_email}</p>
                  <p className="text-slate-500 text-xs truncate max-w-md">{lastUserMessage(c.recent_messages)}</p>
                  <p className="text-slate-600 text-xs mt-0.5">{formatRelative(c.created_at)}</p>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleClaim(c.id)}
                  disabled={!!myCase || claimingId === c.id}
                  className="bg-blue-600 hover:bg-blue-500 text-white text-xs shrink-0"
                  data-testid="support-claim-btn"
                >
                  {claimingId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Tomar caso'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recently answered by me */}
      {answered.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <History className="w-5 h-5 text-emerald-400" /> Respondidos recientemente por mí
          </h2>
          <div className="space-y-2">
            {answered.map((c) => (
              <div key={c.id} className="p-3 bg-slate-800/30 rounded-lg text-sm">
                <p className="text-slate-300">{c.user_email} <span className="text-slate-600">· {formatRelative(c.answered_at)}</span></p>
                <p className="text-slate-500 text-xs mt-1 truncate">{c.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
